"use client";

import { useEffect, useState } from "react";
import { dataUrlToBytes } from "@/lib/data-url";

let workerInitialized = false;

async function initWorker() {
  if (workerInitialized) return;
  const pdfjsLib = await import("pdfjs-dist");
  // Use the copied worker file from /public
  pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  workerInitialized = true;
}

// Caps how many PDFs are fetched/parsed/rendered at once. Without this, a
// tile grid whose thumbnails all become visible together (e.g. on first
// paint) would fire one full-file fetch + pdf.js parse + canvas render per
// tile simultaneously. Queued callers wait for a slot instead of racing.
const MAX_CONCURRENT_RENDERS = 3;
let activeRenders = 0;
const renderQueue: Array<() => void> = [];

function acquireRenderSlot(): Promise<() => void> {
  return new Promise((resolve) => {
    const tryAcquire = () => {
      if (activeRenders < MAX_CONCURRENT_RENDERS) {
        activeRenders++;
        resolve(() => {
          activeRenders--;
          const next = renderQueue.shift();
          if (next) next();
        });
      } else {
        renderQueue.push(tryAcquire);
      }
    };
    tryAcquire();
  });
}

export type PdfThumbnailState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; dataUrl: string }
  | { status: "error" };

export function usePdfThumbnail(
  url: string | undefined,
  enabled: boolean
): PdfThumbnailState {
  const [state, setState] = useState<PdfThumbnailState>({ status: "idle" });

  useEffect(() => {
    if (!enabled || !url) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    (async () => {
      const release = await acquireRenderSlot();
      if (cancelled) {
        release();
        return;
      }
      try {
        await initWorker();
        const pdfjsLib = await import("pdfjs-dist");

        // Always pre-fetch bytes on the main thread — blob URLs are not accessible
        // from the worker thread, and remote URLs may have CORS restrictions.
        // data: URLs (e.g. sent-message attachments) are decoded in memory
        // because CSP connect-src blocks fetch()ing them.
        let data: Uint8Array;
        if (url.startsWith("data:")) {
          data = dataUrlToBytes(url);
        } else {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
          data = new Uint8Array(await response.arrayBuffer());
        }

        if (cancelled) return;

        const loadingTask = pdfjsLib.getDocument({ data });
        const pdf = await loadingTask.promise;

        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        const page = await pdf.getPage(1);

        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        // Scale so the longer dimension fits within 192px (2× 96px grid card)
        const viewport = page.getViewport({ scale: 1 });
        const scale = 192 / Math.max(viewport.width, viewport.height);
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("No 2d context");

        await page.render({ canvas, canvasContext: ctx, viewport: scaledViewport }).promise;

        if (cancelled) {
          await loadingTask.destroy();
          return;
        }

        setState({ status: "ready", dataUrl: canvas.toDataURL("image/jpeg", 0.85) });

        await loadingTask.destroy();
      } catch (err) {
        console.error("[usePdfThumbnail] failed:", err);
        if (!cancelled) setState({ status: "error" });
      } finally {
        release();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, enabled]);

  return state;
}
